import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import { FeedbackButton } from "./Feedback";
import { Song, melodyLabel, subscribeToCatalogue } from "../songs";
import { logEvent, analyticsBlocked } from "../analytics";
import { delistedIds, suppressedIds } from "../suppressions";

const SearchIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M10 4a6 6 0 1 0 3.5 10.9l4.3 4.3 1.4-1.4-4.3-4.3A6 6 0 0 0 10 4zm0 2a4 4 0 1 1 0 8 4 4 0 0 1 0-8z"
      fill="currentColor"
    />
  </svg>
);

const NoteIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z" fill="currentColor" />
  </svg>
);

/* One unified catalogue on the homepage: the curated 1900+ classics and the
   visitor submissions live in the same list, with the same card design. */

type Kind = "klassiker" | "inskickad";

type Item = {
  key: string;
  name: string;
  melody: string;
  url: string;
  snippet: string;
  copyrighted?: boolean;
  full: string; // whole lyric, lowercased, so a half-remembered line finds the song
  kind: Kind;
  occasions: string[];
  len: number;
  explicit: boolean;
  internal: boolean;
  fame: number;
};

type Classic = { n: string; m: string; u: string; o?: string[]; s: string; f?: number; len?: number; x?: number };

/* Guide and collection pages, from the same search-pages.json the nav overlay
   already loads. The homepage searched songs ONLY, so a visitor who typed
   "surstromming" or "spritbolaget" got zero results even though we have and
   rank for both pages - the content existed but only the top-nav search could
   reach it. Kept in its own titled section below the songs: these are
   navigation, not browsing, and must never look like the filter chips. */
type PageHit = { n: string; u: string; t?: string };

type FilterKey =
  | "alla"
  | "klassiker"
  | "inskickade"
  | "kraftskiva"
  | "midsommar"
  | "jul"
  | "korta"
  | "fracka";

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "alla", label: "Alla visor" },
  { key: "klassiker", label: "Kända klassiker" },
  { key: "inskickade", label: "Besökarnas visor" },
  { key: "kraftskiva", label: "Kräftskiva" },
  { key: "midsommar", label: "Midsommar" },
  { key: "jul", label: "Jul" },
  { key: "korta", label: "Korta" },
  { key: "fracka", label: "Fräcka" },
];

const OCCASIONS: { href: string; label: string; emoji: string }[] = [
  { href: "/guide/kraftskiva/", label: "Kräftskiva", emoji: "🦞" },
  { href: "/guide/midsommar/", label: "Midsommar", emoji: "🌼" },
  { href: "/guide/julbord/", label: "Julbord", emoji: "❄️" },
  { href: "/guide/brollop/", label: "Bröllop", emoji: "💍" },
  { href: "/guide/sittning/", label: "Sittning", emoji: "🎓" },
  { href: "/guide/fodelsedag/", label: "Födelsedag", emoji: "🎂" },
  { href: "/guide/nyar/", label: "Nyår", emoji: "🥂" },
  { href: "/sanghafte/", label: "Eget sånghäfte", emoji: "📖" },
];

/* Visitor songs with adult content. These ARE part of "Alla visor" so the count
   is literally complete; the card preview is replaced with a neutral line further
   down in the card renderer, so no explicit lyric is rendered next to an ad.
   Full text lives on the song page. */
const EXPLICIT_IDS = new Set(["21", "23", "27", "31", "44"]);
// Sensitive subjects (9/11, cancer diagnosis) - authentic submissions, listed
// everywhere, but their preview is masked too. Mirrors featurable() in screens.js.
const SENSITIVE_IDS = new Set(["5", "35"]);
// Restored songs whose opening lines would sit beside ads on list pages: keep the
// card (title + melody) but show no preview. Mirrors data/noads.json.
const MASKED_IDS = new Set(["5", "9", "45", "50", "69", "75", "76"]);
// Suppressed songs come from data/suppressions.json, inlined by postbuild as
// window.__SUP__ (see src/suppressions.ts). Two kinds:
//   delisted   - never listed anywhere; the page stays reachable by direct URL,
//                where it explains why the text is not published.
//   copyright  - traced to a named lyricist: list them, never quote them.
// Adding one is a data edit plus `npm run seo` - nothing to change here.
const DELISTED_IDS = new Set<string>(delistedIds());
const SUPPRESSED_IDS = new Set<string>(suppressedIds());
const COPYRIGHT_IDS = new Set<string>(
  suppressedIds().filter((id) => !DELISTED_IDS.has(id))
);

const fold = (v: string): string =>
  (v || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ø/g, "o")
    .replace(/æ/g, "a");

/* A deliberately sloppy fold, used only by the suggestion pass below.
   Collapses the spelling variants Swedish visitors actually type: j/y for i
   (someone searched "Nikolaiev" for our "Nikolajevs"), w for v, ck for k,
   x for ks, z for s, and any doubled letter down to one. */
const looseFold = (v: string): string =>
  fold(v)
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/[jy]/g, "i")
    .replace(/w/g, "v")
    .replace(/ck/g, "k")
    .replace(/x/g, "ks")
    .replace(/z/g, "s")
    .replace(/(.)\1+/g, "$1");

/* True when two words differ by at most one insertion, deletion or
   substitution, so "vater" still finds "valter". Cheap: one linear scan, no
   edit-distance matrix. */
const ed1 = (a: string, b: string): boolean => {
  if (a === b) return true;
  let x = a;
  let y = b;
  if (x.length > y.length) {
    const t = x;
    x = y;
    y = t;
  }
  if (y.length - x.length > 1) return false;
  let i = 0;
  while (i < x.length && x[i] === y[i]) i += 1;
  if (i === x.length) return true;
  if (x.length === y.length) return x.slice(i + 1) === y.slice(i + 1);
  return x.slice(i) === y.slice(i + 1);
};

const snippet = (text?: string, max = 110) => {
  if (!text) return "";
  const flat = text.replace(/\s+/g, " ").trim();
  if (!flat) return "";
  return flat.length > max ? flat.slice(0, max - 3).trimEnd() + "…" : flat;
};

const HOME_FAQ: [string, string][] = [
  ["Vad är en snapsvisa?", "En snapsvisa är en kort svensk sång som sjungs tillsammans precis innan man tar en snaps. Melodin är något alla redan kan, texten är ofta rolig eller rättfram, och visan tar sällan mer än en halv minut."],
  ["När sjunger man snapsvisor?", "Framför allt på kräftskiva i augusti, midsommar i juni och julbord i december – men också på bröllop, födelsedagar, sittningar, svensexor och möhippor."],
  ["Hur många snapsvisor behöver man?", "Tre till fem räcker långt för en middag. Börja med en som alla kan, ta en rolig i mitten och spara den kortaste till sist."],
  ["Kostar Snapsdags något?", "Nej. Alla snapsvisor med text och melodi är helt gratis att läsa, dela och sjunga direkt från mobilen – ingen inloggning behövs."],
  ["Kan jag skicka in en egen snapsvisa?", "Ja! Använd formuläret på sidan Lägg till visa. Visor från besökare är ryggraden i samlingen."],
];

const userToItem = (s: Song): Item => ({
  key: `u-${s.id}`,
  name: s.name,
  melody: s.melody || "",
  url: s.url || `/song/${s.id}/`,
  snippet: SUPPRESSED_IDS.has(String(s.id)) || MASKED_IDS.has(String(s.id)) ? "" : snippet(s.text),
  full: SUPPRESSED_IDS.has(String(s.id)) ? "" : (s.text || "").toLowerCase(),
  copyrighted: COPYRIGHT_IDS.has(String(s.id)),
  kind: "inskickad",
  occasions: [s.category],
  len: (s.text || "").replace(/\s+/g, " ").trim().length,
  explicit: EXPLICIT_IDS.has(String(s.id)) || SENSITIVE_IDS.has(String(s.id)),
  internal: true,
  fame: 0,
});

const classicToItem = (c: Classic): Item => ({
  key: `k-${c.u}`,
  name: c.n,
  melody: c.m || "",
  url: c.u,
  snippet: snippet(c.s),
  full: ((c as any).t || c.s || "").toLowerCase(),
  kind: "klassiker",
  occasions: c.o || [],
  len: c.len || (c.s || "").length,
  // postbuild sets x=1 for crude songs and ships them with an empty preview.
  // Carrying the flag through gives them the same treatment visitor songs
  // already get: the "vuxet sprak" note instead of a blank card, the Frack
  // badge, and inclusion in the Fracka chip.
  explicit: c.x === 1,
  internal: false,
  fame: c.f || 0,
});

const passes = (it: Item, f: FilterKey) => {
  if (f === "fracka") return it.explicit;
  switch (f) {
    case "alla":
      return true;
    case "klassiker":
      return it.kind === "klassiker";
    case "inskickade":
      return it.kind === "inskickad";
    case "korta":
      return it.len > 0 && it.len <= 230;
    default:
      return it.occasions.indexOf(f) >= 0;
  }
};

const SongList: React.FC = () => {
  const [songs, setSongs] = useState<Song[] | null>(null);
  const [classics, setClassics] = useState<Classic[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(40);
  const [pages, setPages] = useState<PageHit[]>([]);
  const pagesAsked = useRef(false);
  const navigate = useNavigate();

  /* The URL is the single source of truth for the browsing state. Deriving the
     filter and query straight from the query string (rather than mirroring them
     into useState) is what makes the browser Back button work: returning from a
     song page restores /?filter=jul or /?q=helan with no extra bookkeeping. */
  const [params, setParams] = useSearchParams();
  const rawFilter = params.get("filter") || "alla";
  const filter: FilterKey = (FILTERS.some((f) => f.key === rawFilter)
    ? rawFilter
    : "alla") as FilterKey;
  const search = params.get("q") || "";

  /* replace: true - typing must not push one history entry per keystroke, and a
     filter change is a refinement of the same page, not a new destination. */
  const setUrlState = (next: { filter?: FilterKey; q?: string }) => {
    const p = new URLSearchParams(params);
    if (next.filter !== undefined) {
      if (next.filter === "alla") p.delete("filter");
      else p.set("filter", next.filter);
    }
    if (next.q !== undefined) {
      if (next.q === "") p.delete("q");
      else p.set("q", next.q);
    }
    setParams(p, { replace: true });
  };
  const setSearch = (v: string) => setUrlState({ q: v });
  const setFilter = (v: FilterKey) => setUrlState({ filter: v });

  useEffect(() => {
    subscribeToCatalogue(setSongs, setError);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch("/home-songs.json")
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        if (!cancelled) setClassics(Array.isArray(d) ? d : []);
      })
      .catch(() => {
        if (!cancelled) setClassics([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* Most visitors arrive from Google looking for a song everybody knows, so the
     famous classics lead the default list (most famous first). The authentic
     visitor submissions follow immediately, then the rest of the catalogue.
     This also makes the hydrated order match the prerendered HTML. */
  const items = useMemo(() => {
    const cls = (classics || []).map(classicToItem);
    const usr = (songs || [])
      // Never list songs without published lyrics (copyright or delist suppressions).
      .filter((x) => !DELISTED_IDS.has(String(x.id)) && !SUPPRESSED_IDS.has(String(x.id)))
      .map(userToItem)
      .reverse();
    const famous = cls
      .map((c, i) => ({ c, i }))
      .sort((a, b) => a.c.fame - b.c.fame || a.i - b.i)
      .map((x) => x.c)
      .filter((c) => c.fame > 0); // fame is a rank: 1 = most famous, 0 = unranked
    const rest = cls.filter((c) => c.fame === 0);
    return [...famous, ...usr, ...rest];
  }, [classics, songs]);

  const visible = useMemo(() => {
    const q = fold(search.trim());
    // An active search always covers the complete catalogue. The filter chips
    // only shape the default browsing list; they must never hide a song from
    // someone who typed its name (see "Lovisa rask", a fräck visitor song).
    if (q === "") return items.filter((it) => passes(it, filter));
    const scope = items;
    // Match each word on its own rather than as one contiguous phrase. People
    // half-remember a line and type the words in the wrong order ("armen
    // vinkel", "mas klyvarbom"); demanding the exact phrase returned nothing.
    // A true phrase hit still outranks scattered words.
    const toks = q.split(/\s+/).filter((t) => t.length > 0);
    const hasAll = (hay: string): boolean => toks.every((t) => hay.includes(t));
    // Rank matches so the song you typed the name of comes first.
    const score = (it: Item): number => {
      const n = fold(it.name);
      if (n === q) return 0;
      if (n.startsWith(q)) return 1;
      if (n.includes(q)) return 2;
      if (hasAll(n)) return 3;
      const m = fold(it.melody);
      if (m.includes(q) || hasAll(m)) return 4;
      if (hasAll(fold(it.snippet))) return 5;
      if (hasAll(fold(it.full))) return 6;
      return 99;
    };
    return scope
      .map((it, i) => ({ it, i, r: score(it) }))
      .filter((x) => x.r < 99)
      .sort((a, b) => a.r - b.r || a.i - b.i)
      .map((x) => x.it);
  }, [items, filter, search]);

  const counts = useMemo(() => {
    const c: Partial<Record<FilterKey, number>> = {};
    FILTERS.forEach((f) => (c[f.key] = items.filter((it) => passes(it, f.key)).length));
    return c;
  }, [items]);

  const loading = songs === null && classics === null;
  /* loading stays AND so the baked classics paint immediately. The "no match"
     CTA, though, must wait for BOTH sources: classics are baked into
     home-songs.json and arrive first while visitor songs come from RTDB, so in
     between, a search for a visitor visa told the visitor we did not have a
     visa we do have. The searchlog guard below is OR for the same reason, so
     that false miss was never even recorded. If the RTDB subscription errors we
     let the CTA through rather than hiding it forever. */
  const catalogueComplete =
    (songs !== null && classics !== null) || error !== null;
  const shown = visible.slice(0, limit);

  /* The filter and query ride in the URL, but how far the visitor had scrolled
     and how many "Visa fler" pages they had opened do not. Keep those per-URL in
     sessionStorage so Back from a song page lands on the same row, not the top.
     Restored once per mount: re-running it on every filter change would yank a
     browsing visitor to a stale offset. */
  const stateKey = `sl:${params.toString()}`;
  const restoredRef = useRef(false);
  /* Queries already sent to the searchlog, so a re-run after the catalogue
     finishes loading does not log the same query twice. */
  const loggedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (restoredRef.current || items.length === 0) return;
    restoredRef.current = true;
    let saved: { y: number; limit: number } | null = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(stateKey) || "null");
    } catch {
      saved = null;
    }
    if (!saved || !saved.y) return;
    const y = saved.y;
    if (saved.limit > limit) setLimit(saved.limit);
    // Let the extra rows paint before jumping, or the target row is not there yet.
    const t = window.setTimeout(() => window.scrollTo(0, y), 0);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, stateKey]);

  const rememberPosition = () => {
    try {
      sessionStorage.setItem(stateKey, JSON.stringify({ y: window.scrollY, limit }));
    } catch {
      /* private mode / quota - position is a nicety, never break navigation */
    }
  };

  // Measure what visitors look for on the homepage (debounced, no PII).
  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 3) return;
    /* Never log while the catalogue is still arriving. items is
       (classics || []) + (songs || []), so a query typed before both fetches
       resolve scores against a partial list and would be written down as a
       zero-result search for a song we actually have. That signal decides what
       we source next, so a missing row is much cheaper than a false one. This
       effect re-runs on items.length, so the query is logged once the
       catalogue is complete. */
    if (songs === null || classics === null) return;
    if (loggedRef.current.has(q)) return;
    const t = setTimeout(() => {
      loggedRef.current.add(q);
      logEvent("Home", "Search", q.slice(0, 40) + " | " + visible.length);
      // The searchlog drives which songs we source next, so our own QA passes
      // must never write to it (same guardrail as GA4: ?noga=1, iframes,
      // webdriver/headless, localhost).
      if (analyticsBlocked()) return;
      // Also log to our own store: GA4 custom dimensions are not registered on
      // this property, so event labels are not queryable. This gives us the
      // exact terms visitors look for — above all the ones with zero results.
      try {
        /* n counts matching songs, p matching guides/collections. A query like
           "surstromming" or "spritbolaget" matches no song but does land on a
           good page, and logging only n made those look like unmet demand. */
        const body = JSON.stringify({
          q: q.slice(0, 60),
          n: visible.length,
          p: pageHits.length,
          t: Date.now(),
        });
        fetch(
          "https://snapsdags-default-rtdb.europe-west1.firebasedatabase.app/searchlog.json",
          { method: "POST", body: body, keepalive: true }
        ).catch(() => {});
      } catch (e) {
        /* logging must never break search */
      }
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, items.length]);

  useEffect(() => {
    setLimit(40);
  }, [filter, search]);

  /* Fetched lazily on the first real search so simply browsing the homepage
     stays one request. 64 entries, so this single fetch serves every later
     query the visitor types. */
  useEffect(() => {
    if (search.trim() === "" || pagesAsked.current) return;
    pagesAsked.current = true;
    fetch("/search-pages.json")
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setPages(Array.isArray(d) ? d : []))
      .catch(() => {
        /* Pages are a bonus; song search must keep working regardless. */
        pagesAsked.current = false;
      });
  }, [search]);

  const pageHits = useMemo(() => {
    const q = fold(search.trim());
    if (q === "" || pages.length === 0) return [] as PageHit[];
    const toks = q.split(/\s+/).filter((t) => t.length > 0);
    const score = (pg: PageHit): number => {
      const n = fold(pg.n);
      if (n === q) return 0;
      if (n.startsWith(q)) return 1;
      if (n.includes(q)) return 2;
      if (toks.every((t) => n.includes(t))) return 3;
      const hay = fold(pg.n + " " + (pg.t || "") + " " + pg.u.replace(/[-/]/g, " "));
      if (toks.every((t) => hay.includes(t))) return 4;
      return 99;
    };
    return pages
      .map((pg, idx) => ({ pg, idx, r: score(pg) }))
      .filter((x) => x.r < 99)
      .sort((x, y) => x.r - y.r || x.idx - y.idx)
      .slice(0, 6)
      .map((x) => x.pg);
  }, [pages, search]);

  /* A tolerant second pass that runs ONLY when the strict search above found
     nothing at all. A zero-result screen is where we lose a visitor, and the
     searchlog shows real people missing songs we do own by one letter. Because
     it never runs while genuine matches exist, it cannot reorder or dilute a
     working search. */
  const suggestions = useMemo(() => {
    const q = looseFold(search.trim());
    if (q.length < 3 || visible.length > 0) return [] as Item[];
    const toks = q.split(" ").filter((t) => t.length > 0);
    const near = (t: string, w: string): boolean =>
      w.includes(t) || (t.length >= 4 && ed1(t, w));
    const score = (it: Item): number => {
      const name = looseFold(it.name);
      const head = name + " " + looseFold(it.melody);
      if (name.includes(q)) return 1;
      if (head.includes(q)) return 2;
      const hw = head.split(" ");
      if (toks.every((t) => hw.some((w) => near(t, w)))) return 3;
      const tw = looseFold(it.full).split(" ");
      if (toks.every((t) => tw.some((w) => near(t, w)))) return 4;
      return 99;
    };
    return items
      .map((it, i) => ({ it, i, r: score(it) }))
      .filter((x) => x.r < 99)
      .sort((x, y) => x.r - y.r || x.i - y.i)
      .slice(0, 8)
      .map((x) => x.it);
  }, [items, search, visible.length]);

  return (
    <>
      <Helmet>
        {/* title intentionally left to the prerendered HTML so it is not overwritten */}
        <meta
          name="description"
          content="Gratis samling av svenska snapsvisor med text och melodi – kända klassiker och visor från besökare, för kräftskiva, midsommar, jul och året runt."
        />
        <link rel="canonical" href="https://snapsdags.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Snapsvisor – text och melodi till svenska snapsvisor" />
        <meta property="og:url" content="https://snapsdags.com/" />
      </Helmet>

      <header className="sl-hero">
        <div className="sd-wrap">
          <h1>Svenska snapsvisor – text och melodi</h1>
          <p>
            {counts.alla
              ? `${counts.alla} snapsvisor: kända klassiker och visor som besökare skickat in. Helt gratis, direkt i mobilen.`
              : "Kända klassiker och visor som besökare skickat in. Helt gratis, direkt i mobilen."}
          </p>
          {items.length > 0 && (
            <button
              type="button"
              className="sd-btn-gold"
              onClick={() => {
                const pool = visible.length ? visible : items;
                const pick = pool[Math.floor(Math.random() * pool.length)];
                if (pick.internal) navigate(pick.url);
                else window.location.href = pick.url;
              }}
            >
              Slumpa fram en visa
            </button>
          )}
        </div>
      </header>

      <div className="sd-wrap sl-main">
        <div className="sl-search">
          <SearchIcon />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Sök på visa, melodi eller textrad"
            aria-label="Sök snapsvisa"
          />
        </div>

        <div className="sl-row sl-chips" role="group" aria-label="Filtrera visor">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className="sl-chip"
              aria-pressed={filter === f.key}
              onClick={() => {
                setFilter(f.key);
                logEvent("Home", "Filter", f.key);
              }}
            >
              {counts[f.key] ? `${f.label} (${counts[f.key]})` : f.label}
            </button>
          ))}
        </div>

        {error && <p className="sl-err">{error}</p>}

        {loading ? (
          <ul className="sl-skel" aria-hidden="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <li key={i} />
            ))}
          </ul>
        ) : (
          <ul className="sl-grid">
            {shown.map((it) => {
              const inner = (
                <>
                  <h2>{it.name}</h2>
                  <p className="sl-mel">
                    <NoteIcon /> {melodyLabel(it.melody)}
                  </p>
                  {it.snippet && !it.explicit && !it.copyrighted && (
                    <p className="sl-snip">{it.snippet}</p>
                  )}
                  {it.copyrighted && (
                    <p className="sl-snip">
                      Texten har kända upphovsmän och publiceras inte här.
                    </p>
                  )}
                  {it.explicit && (
                    <p className="sl-snip">
                      Fräck visa med vuxet språk – texten finns på visans egen sida.
                    </p>
                  )}
                  <span className={it.kind === "klassiker" ? "sl-tag is-classic" : "sl-tag"}>
                    {it.kind === "klassiker" ? "Känd klassiker" : "Inskickad visa"}
                  </span>
                  {it.explicit && <span className="sl-tag">Fräck</span>}
                </>
              );
              return (
                <li key={it.key}>
                  <div className="sl-card">
                    {it.internal ? (
                      <Link to={it.url} onClick={rememberPosition}>
                        {inner}
                      </Link>
                    ) : (
                      <a href={it.url} onClick={rememberPosition}>
                        {inner}
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {visible.length > shown.length && (
          <div className="sl-more">
            <button type="button" className="sd-btn-out" onClick={() => setLimit((l) => l + 60)}>
              Visa fler visor ({visible.length - shown.length} kvar)
            </button>
          </div>
        )}

        {pageHits.length > 0 && (
          <section className="sl-guides" aria-labelledby="home-pagehits">
            <h2 id="home-pagehits">Sidor som matchar</h2>
            <p className="sl-guides-lead">
              Guider och samlingar på sajten som handlar om det du sökte.
            </p>
            <div className="sl-guide-grid">
              {pageHits.map((pg) => (
                <a
                  key={pg.u}
                  href={pg.u}
                  onClick={() => logEvent("Home", "PageHitClick", pg.u)}
                >
                  <span className="sl-guide-emoji" aria-hidden="true">
                    📄
                  </span>
                  <span className="sl-guide-label">{pg.n}</span>
                </a>
              ))}
            </div>
          </section>
        )}

        {suggestions.length > 0 && (
          <section className="sl-guides" aria-labelledby="home-suggest">
            <h2 id="home-suggest">Menade du någon av dessa?</h2>
            <p className="sl-guides-lead">
              Ingen exakt träff – men de här visorna ligger nära det du skrev.
            </p>
            <div className="sl-guide-grid">
              {suggestions.map((it) =>
                it.internal ? (
                  <Link
                    key={it.key}
                    to={it.url}
                    onClick={() => {
                      logEvent("Home", "SuggestClick", it.name);
                      rememberPosition();
                    }}
                  >
                    <span className="sl-guide-emoji" aria-hidden="true">
                      🎵
                    </span>
                    <span className="sl-guide-label">{it.name}</span>
                  </Link>
                ) : (
                  <a
                    key={it.key}
                    href={it.url}
                    onClick={() => logEvent("Home", "SuggestClick", it.name)}
                  >
                    <span className="sl-guide-emoji" aria-hidden="true">
                      🎵
                    </span>
                    <span className="sl-guide-label">{it.name}</span>
                  </a>
                )
              )}
            </div>
          </section>
        )}

        {!loading && catalogueComplete && visible.length === 0 && (
          <div className="sl-empty">
            <p>
              Ingen visa matchade{search.trim() ? ` ”${search.trim()}”` : " din sökning"} – men
              kan du den, så lägger vi upp den.
            </p>
            {/* Carry the search term into the form so the visitor does not have to
                type it again. Mirrors the zero-result CTA in search-overlay.js. */}
            <Link
              className="sd-btn-out"
              to={`/new/${search.trim() ? `?namn=${encodeURIComponent(search.trim().slice(0, 60))}` : ""}`}
              onClick={() => logEvent("Home", "MissingSongAsk", search.trim().slice(0, 60))}
            >
              Skicka in visan
            </Link>
          </div>
        )}

        <div className="sl-foot">
          <p>
            Saknas din favoritvisa?{" "}
            <Link to="/new/" style={{ fontWeight: 600 }}>
              Skicka in den
            </Link>{" "}
            – det tar tio sekunder.
          </p>
          <FeedbackButton
            context="home"
            type="song_request"
            label="Saknas en visa?"
            title="Vilken visa saknas?"
            placeholder="Skriv namnet på visan (och texten om du har den) – vi lägger till den."
          />
        </div>

        {/* Editorial guides live in their own titled section. They used to sit
            directly under the search box styled as pills, where visitors read
            them as filter chips and could not tell browsing from navigation. */}
        <section className="sl-guides" aria-labelledby="home-guides">
          <h2 id="home-guides">Guider till olika tillfällen</h2>
          <p className="sl-guides-lead">
            Längre läsning med färdiga visval, ordning och tips för just din fest.
          </p>
          <div className="sl-guide-grid">
            {OCCASIONS.map((o) => (
              <a key={o.href} href={o.href}>
                <span className="sl-guide-emoji" aria-hidden="true">{o.emoji}</span>
                <span className="sl-guide-label">{o.label}</span>
              </a>
            ))}
          </div>
        </section>

        <section className="sl-faq" aria-labelledby="home-faq">
          <h2 id="home-faq">Vanliga frågor om snapsvisor</h2>
          {HOME_FAQ.map(([q, a]) => (
            <div key={q}>
              <h3>{q}</h3>
              <p>{a}</p>
            </div>
          ))}
        </section>
      </div>

    </>
  );
};

export default SongList;
